/*****************************************************************
 *  CUSTOMER BUTLER - ONE CUSTOMER / ONE PROPERTY, ZOHO'S PAGE     *
 *  File: apps/web/src/pages/customer/ZohoRecordPages.js           *
 *  Addresses: /app/customers/<id>, /app/properties/<id> (HR look) *
 *  What it does: the record page built the way Zoho's record page *
 *  is built, in HR Butler's look:                                 *
 *    left: the "Related List" rail (jumps) + a small summary      *
 *    right: the title row (back, name, Add Tags, buttons), the    *
 *    Overview / Timeline switch with "Last update", then          *
 *    Overview = summary card, Primary contact | Next action,      *
 *    Hide/Show Details (every section, two columns), then every   *
 *    related list as a block in the rail's order;                 *
 *    Timeline = the Activities list (logged + other Butlers).     *
 *  Reads: the record, layout, notes, consent, reports, tasks and  *
 *  timeline doors.  Writes: only through the doors the blocks and *
 *  the field editor already use (who / when / why kept).          *
 *****************************************************************/

/* WHY (2026-10-15, James via Fable): the HR look stays ("you got that part
   right"); the record page STRUCTURE copies Zoho's record page as his team
   sees it (screenshots 02:44, 02:47, 02:48 of a Deals/Jobs record). The
   folded-card record of 50b36ab (HrRecordPages.js) is kept in the code,
   untouched, but no longer drawn. */

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 - SMALL LOADERS (notes count, consent, reports)      *
 *  SECTION 2 - JUMPING TO A BLOCK                                 *
 *  SECTION 3 - ONE BLOCK FOR ONE RAIL ENTRY                       *
 *  SECTION 4 - THE CUSTOMER RECORD                                *
 *  SECTION 5 - THE PROPERTY RECORD                                *
 *****************************************************************/

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Icon } from './customerIcons';
import { HrPageFrame, HrBanner, HrQuietLine } from './HrChrome';
import { useSaveOneField } from './RecordSections';
import { streetWords } from './RelatedLists';
import { useCustomerRecord, propertyAddresses } from './CustomerRecordPage';
import { usePropertyRecord } from './PropertyRecordPage';
import { useCustomerAccessView } from './customerAccessView';
import { formatMoment } from './customerDates';
import { useRecordActivities } from './recordActivities';
import {
  RelatedRail, PhoneRelatedCard, TitleRow, ViewSwitch, StageStrip, SummaryCard,
  SidePanels, PersonPanel, LatestThingPanel, NextActionPanel, DetailsBlock,
  describeLastUpdate, VIEW_OVERVIEW, VIEW_TIMELINE,
} from './ZohoRecordParts';
import {
  blockIdFor, NotConnectedBlock, NotesBlock, PropertiesBlock, ReportsBlock,
  OpenActivitiesBlock, ClosedActivitiesBlock, ContactsBlock, PeopleBlock, ConsentBlock,
  peopleOnProperty, sharesTheOtherBlock, OtherZohoListsBlock, HistoryTimeline,
} from './ZohoRelatedBlocks';
import { propertyAlertWords } from './HrListPages';
import { saveCustomer, saveProperty, fetchOfficeNotes, fetchConsent, fetchPastReports } from './customerApi';

/*****************************************************************
 *  SECTION 1 - SMALL LOADERS                                      *
 *****************************************************************/

/* ---- useDoorAnswer(doorCall, whenToAsk) --------------------------
   Out: [answer (null while loading, [] or the door's answer), reload,
   keepNewAnswer]. keepNewAnswer(list) takes a fresher answer that a
   block on the page already loaded (the notes board), so the page
   does not ask the door a second time.
   A door that says no gives null-safe [] (the block says so).   */
function useDoorAnswer(doorCall, whenToAsk) {
  const [answer, setAnswer] = useState(null);

  const reload = useCallback(async () => {
    try {
      setAnswer(await doorCall());
    } catch (doorProblem) {
      setAnswer([]);
    }
  }, whenToAsk); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    reload();
  }, [reload]);

  return [answer, reload, setAnswer];
}

/*****************************************************************
 *  SECTION 2 - JUMPING TO A BLOCK                                 *
 *****************************************************************/

/* ---- scrollToBlock(railEntry) -- brings the block on screen ---- */
function scrollToBlock(railEntry) {
  const blockElement = document.getElementById(blockIdFor(railEntry));

  if (blockElement && blockElement.scrollIntoView) {
    blockElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

/* ---- useJumper(setView) -- jump = Overview first, then scroll ---- */
function useJumper(setView) {
  const [pendingJump, setPendingJump] = useState(null);

  useEffect(() => {
    if (!pendingJump) {
      return undefined;
    }

    const scrollTimer = setTimeout(() => {
      scrollToBlock(pendingJump.entry);

      if (pendingJump.afterJump) {
        pendingJump.afterJump();
      }
    }, 60);

    return () => clearTimeout(scrollTimer);
  }, [pendingJump]);

  return useCallback((railEntry, afterJump) => {
    setView(VIEW_OVERVIEW);
    setPendingJump({ entry: railEntry, afterJump, at: Date.now() });
  }, [setView]);
}

/*****************************************************************
 *  SECTION 3 - ONE BLOCK FOR ONE RAIL ENTRY                       *
 *****************************************************************/

/* ---- RailBlock({ railEntry, parts }) -----------------------------
   parts: everything the blocks may need (record, counts, loaders).
   A list with no Butler block is shown muted, "Not connected yet". */
function RailBlock({ railEntry, parts }) {
  const blockId = blockIdFor(railEntry);
  const title = railEntry.label;

  switch (railEntry.block) {
    case 'notes':
      return (
        <NotesBlock
          blockId={blockId}
          title={title}
          count={parts.notesCount}
          customerId={parts.customerId}
          onNotesLoaded={parts.keepNewNotes}
        />
      );
    case 'properties':
      return (
        <PropertiesBlock
          blockId={blockId}
          title={title}
          customerId={parts.customerId}
          properties={parts.properties}
          onChanged={parts.reload}
          addRequest={parts.addPropertyRequest}
        />
      );
    case 'reports':
      return <ReportsBlock blockId={blockId} title={title} reportAnswer={parts.reportAnswer} />;
    case 'open-activities':
      return <OpenActivitiesBlock blockId={blockId} title={title} sorted={parts.sortedActivities} />;
    case 'closed-activities':
      return (
        <ClosedActivitiesBlock
          blockId={blockId}
          title={title}
          sorted={parts.sortedActivities}
          customerId={parts.customerId}
          onLogged={parts.reloadActivities}
        />
      );
    case 'contacts':
      return <ContactsBlock blockId={blockId} title={title} customerId={parts.customerId} contacts={parts.contacts} onChanged={parts.reload} />;
    case 'people':
      return <PeopleBlock blockId={blockId} title={title} owner={parts.owner} linkedPeople={parts.linkedPeople} />;
    case 'consent':
      return <ConsentBlock blockId={blockId} title={title} customerId={parts.customerId} records={parts.consentRecords} onChanged={parts.reloadConsent} />;
    default:
      return <NotConnectedBlock blockId={blockId} title={title} />;
  }
}

/* ---- railCountFor(railEntry, parts) -- the number beside a rail link ---- */
function railCountFor(railEntry, parts) {
  const sorted = parts.sortedActivities;

  switch (railEntry.block) {
    case 'notes':
      return parts.notesCount;
    case 'properties':
      return (parts.properties || []).length;
    case 'reports':
      return parts.reportAnswer && parts.reportAnswer.state === 'ok' ? parts.reportAnswer.reports.length : undefined;
    case 'open-activities':
      return sorted ? sorted.open.tasks.length + sorted.open.meetings.length + sorted.open.calls.length : undefined;
    case 'closed-activities':
      return sorted ? sorted.closed.tasks.length + sorted.closed.meetings.length + sorted.closed.calls.length : undefined;
    case 'contacts':
      return (parts.contacts || []).length;
    case 'people':
      return peopleOnProperty(parts.owner, parts.linkedPeople).length;
    case 'consent':
      return Array.isArray(parts.consentRecords) ? parts.consentRecords.length : undefined;
    default:
      return undefined;
  }
}

/* ---- RecordBody(bodyParts) -- the shared two-column body ---------
   In: { rail, parts, view, setView, jump, summaryTitle, summaryFacts,
         titleRow, overviewTop, timeline, lastUpdateWords }.
   Blocks: each list with a Butler block or a block of its own, in
   the rail's order; then the other Zoho lists named in one block. */
function RecordBody(bodyParts) {
  const { rail, parts, view, setView, jump, summaryTitle, summaryFacts, titleRow, overviewTop, timeline, lastUpdateWords } = bodyParts;
  const countFor = (railEntry) => railCountFor(railEntry, parts);

  return (
    <div className="cb-zr">
      <RelatedRail rail={rail} countFor={countFor} onJump={jump} summaryTitle={summaryTitle} summaryFacts={summaryFacts} />
      <div className="cb-zr-main">
        {titleRow}
        <PhoneRelatedCard rail={rail} countFor={countFor} onJump={jump} />
        <ViewSwitch view={view} onChoose={setView} lastUpdateWords={lastUpdateWords} />
        {view === VIEW_TIMELINE ? <div className="cb-zr-card cb-zr-timeline">{timeline}</div> : (
          <>
            <StageStrip pipeline={null} />
            {overviewTop}
            {rail
              .filter((railEntry) => !sharesTheOtherBlock(railEntry))
              .map((railEntry) => <RailBlock key={railEntry.label} railEntry={railEntry} parts={parts} />)}
            <OtherZohoListsBlock labels={rail.filter(sharesTheOtherBlock).map((railEntry) => railEntry.label)} />
          </>
        )}
      </div>
    </div>
  );
}

/* ---- butlerRecordFacts(record) -- the summary under the rail ---- */
function butlerRecordFacts(record) {
  const customValues = record.customFields || {};
  const cameFromZoho = Boolean(customValues.Zoho_Record_Id || customValues.original_zoho_customer_id);

  return [
    { label: 'Added to Butler', value: formatMoment(record.createdAt) },
    { label: 'Last changed', value: formatMoment(record.updatedAt) },
    { label: 'Came from', value: cameFromZoho ? 'Zoho (through the Bridge)' : 'Typed in Butler' },
  ];
}

/* ---- latestReportPanelParts(reportAnswer) -- the "Reports" panel's words ---- */
function latestReportPanelParts(reportAnswer) {
  if (!reportAnswer) {
    return { name: '', emptyWords: 'Looking for past reports' };
  }

  if (reportAnswer.state !== 'ok') {
    return { name: '', emptyWords: reportAnswer.state === 'not-connected' ? 'Past reports not connected yet.' : 'No past reports found.' };
  }

  const newestReport = reportAnswer.reports[0];

  if (!newestReport) {
    return { name: '', emptyWords: 'No past reports found.' };
  }

  const isSent = String(newestReport.status || '').toLowerCase() === 'sent';

  return {
    name: newestReport.address || 'Report',
    href: newestReport.url || '',
    chip: isSent ? { tone: 'ok', icon: 'check', word: 'Sent' } : { tone: 'neutral', icon: 'file', word: newestReport.status || 'Report' },
  };
}

/* ---- latestPropertyPanelParts(properties) -- the "Properties" panel's words ---- */
function latestPropertyPanelParts(properties) {
  const newestFirst = [...(properties || [])].sort((first, second) => String(second.createdAt || '').localeCompare(String(first.createdAt || '')));
  const newestProperty = newestFirst[0];

  if (!newestProperty) {
    return { name: '', emptyWords: 'No properties yet.' };
  }

  const alertCount = propertyAlertWords(newestProperty).length;

  return {
    name: newestProperty.label || streetWords(newestProperty) || 'Unnamed property',
    linkTo: `/properties/${newestProperty.id}`,
    chip: alertCount === 0
      ? { tone: 'neutral', icon: 'check', word: 'No alerts' }
      : { tone: 'warn', icon: 'warn', word: alertCount === 1 ? '1 alert' : `${alertCount} alerts` },
  };
}

/*****************************************************************
 *  SECTION 4 - THE CUSTOMER RECORD                                *
 *****************************************************************/

/* ---- ZohoCustomerRecordPage({ onChooseSkin }) -------------------- */
export function ZohoCustomerRecordPage({ onChooseSkin }) {
  const { id: customerId } = useParams();
  const { customer, layout, problemWords, reload, setCustomer } = useCustomerRecord(customerId);
  const saveOneField = useSaveOneField(saveCustomer, customerId, setCustomer);
  const accessView = useCustomerAccessView();
  const [view, setView] = useState(VIEW_OVERVIEW);
  const jump = useJumper(setView);
  const [addPropertyRequest, setAddPropertyRequest] = useState(0); // raised by "Add a property"
  // WHY (2026-10-15, Fable/James, round-2 rough edge b): the notes board
  // hands its list back through keepNewNotes each time it loads, so the
  // rail's Notes count moves when a note is added or taken off.
  const [notes, , keepNewNotes] = useDoorAnswer(() => fetchOfficeNotes(customerId), [customerId]);
  const [consentRecords, reloadConsent] = useDoorAnswer(() => fetchConsent(customerId), [customerId]);
  const { sorted: sortedActivities, reload: reloadActivities } = useRecordActivities({ customerId });
  const addressKey = customer ? propertyAddresses(customer).join('|') : '';
  const [reportAnswer] = useDoorAnswer(
    () => (customer ? fetchPastReports({ email: customer.primaryEmail || '', addresses: propertyAddresses(customer) }) : new Promise(() => {})),
    [customerId, Boolean(customer), customer && customer.primaryEmail, addressKey],
  );

  const backLink = <Link className="cb-hr-back" to="/customers"><Icon name="arrowleft" /> Back to customers</Link>;

  if (problemWords) {
    return <HrPageFrame onChooseSkin={onChooseSkin}>{backLink}<HrBanner tone="bad" icon="warn">{problemWords}</HrBanner></HrPageFrame>;
  }

  if (!customer || !layout) {
    return <HrPageFrame onChooseSkin={onChooseSkin}>{backLink}<HrQuietLine icon="clock">Loading the customer</HrQuietLine></HrPageFrame>;
  }

  const rail = layout.relatedRail || [];
  const notesRail = rail.find((railEntry) => railEntry.block === 'notes');
  const propertiesRail = rail.find((railEntry) => railEntry.block === 'properties');
  const liveNotes = Array.isArray(notes) ? notes.filter((oneNote) => !oneNote.removedAt) : null;

  /* ---- startANote() -- jump to Notes, put the cursor in its box ---- */
  function startANote() {
    jump(notesRail, () => {
      const noteBox = document.getElementById('cb-note-new');

      if (noteBox) {
        noteBox.focus();
      }
    });
  }

  /* ---- startAProperty() -- jump to Properties and open its Add sheet ---- */
  function startAProperty() {
    jump(propertiesRail, () => setAddPropertyRequest((countNow) => countNow + 1));
  }

  const parts = {
    customerId,
    reload,
    properties: customer.properties || [],
    contacts: customer.contacts || [],
    notesCount: liveNotes ? liveNotes.length : undefined,
    keepNewNotes,
    consentRecords,
    reloadConsent,
    reportAnswer,
    sortedActivities,
    reloadActivities,
    addPropertyRequest,
  };

  const customValues = customer.customFields || {};
  const accountWords = customer.companyName || customValues.Account_Name || '';

  const actions = (
    <>
      {accessView.mayWrite && notesRail ? (
        <button type="button" className="bc-btn is-primary bc-btn-auto" onClick={startANote}>
          <Icon name="pencil" size="button" /> Add a note
        </button>
      ) : null}
      {accessView.mayWrite && propertiesRail ? (
        <button type="button" className="bc-btn is-ghost bc-btn-auto" onClick={startAProperty}>
          <Icon name="home" size="button" /> Add a property
        </button>
      ) : null}
      {accessView.mayChangeSetup ? (
        <Link className="bc-btn is-ghost bc-btn-auto" to="/crm-setup"><Icon name="gear" size="button" /> CRM Setup</Link>
      ) : null}
    </>
  );

  return (
    <HrPageFrame onChooseSkin={onChooseSkin}>
      <RecordBody
        rail={rail}
        parts={parts}
        view={view}
        setView={setView}
        jump={jump}
        summaryTitle="Butler record"
        summaryFacts={butlerRecordFacts(customer)}
        lastUpdateWords={describeLastUpdate(customer.updatedAt)}
        titleRow={(
          <TitleRow
            backTo="/customers"
            backWords="Back to customers"
            title={customer.displayName || 'Unnamed customer'}
            account={accountWords}
            record={customer}
            layout={layout}
            onSaveField={saveOneField}
            actions={actions}
          />
        )}
        overviewTop={(
          <>
            <SummaryCard record={customer} layout={layout} onSaveField={saveOneField} />
            <SidePanels>
              <LatestThingPanel title="Properties" {...latestPropertyPanelParts(customer.properties)} />
              <NextActionPanel sorted={sortedActivities} />
              <LatestThingPanel title="Reports" {...latestReportPanelParts(reportAnswer)} />
            </SidePanels>
            <DetailsBlock record={customer} sections={layout.sections} onSaveField={saveOneField} />
          </>
        )}
        timeline={<HistoryTimeline days={sortedActivities ? sortedActivities.history : null} customerId={customerId} onLogged={reloadActivities} />}
      />
    </HrPageFrame>
  );
}

/*****************************************************************
 *  SECTION 5 - THE PROPERTY RECORD                                *
 *****************************************************************/

/* ---- ZohoPropertyRecordPage({ onChooseSkin }) -------------------- */
export function ZohoPropertyRecordPage({ onChooseSkin }) {
  const { id: propertyId } = useParams();
  const { property, layout, problemWords, setProperty } = usePropertyRecord(propertyId);
  const saveOneField = useSaveOneField(saveProperty, propertyId, setProperty);
  const [view, setView] = useState(VIEW_OVERVIEW);
  const jump = useJumper(setView);
  const { sorted: sortedActivities } = useRecordActivities({ propertyId });
  const lookupAddress = property ? (property.addressLine1 || property.label || streetWords(property)) : '';
  const [reportAnswer] = useDoorAnswer(
    () => (property ? fetchPastReports({ email: '', addresses: [lookupAddress] }) : new Promise(() => {})),
    [propertyId, lookupAddress],
  );

  const backLink = <Link className="cb-hr-back" to="/properties"><Icon name="arrowleft" /> Back to properties</Link>;

  if (problemWords) {
    return <HrPageFrame onChooseSkin={onChooseSkin}>{backLink}<HrBanner tone="bad" icon="warn">{problemWords}</HrBanner></HrPageFrame>;
  }

  if (!property || !layout) {
    return <HrPageFrame onChooseSkin={onChooseSkin}>{backLink}<HrQuietLine icon="clock">Loading the property</HrQuietLine></HrPageFrame>;
  }

  const rail = layout.relatedRail || [];
  const owner = property.owner || null;
  const nameWords = property.label || streetWords(property) || 'Unnamed property';
  const placeWords = [streetWords(property), property.zip].filter(Boolean).join(' ');

  const parts = {
    owner,
    linkedPeople: property.linkedPeople || [],
    reportAnswer,
    sortedActivities,
    reloadActivities: () => {},
  };

  const person = owner ? {
    name: owner.displayName || 'Unnamed customer',
    company: '',
    email: owner.primaryEmail || '',
    phone: owner.primaryPhone || '',
    linkTo: `/customers/${owner.id}`,
  } : null;

  const actions = owner ? (
    <Link className="bc-btn is-quiet bc-btn-auto" to={`/customers/${owner.id}`}>
      <Icon name="user" size="button" /> Open the owner
    </Link>
  ) : null;

  return (
    <HrPageFrame onChooseSkin={onChooseSkin}>
      <RecordBody
        rail={rail}
        parts={parts}
        view={view}
        setView={setView}
        jump={jump}
        summaryTitle="Butler record"
        summaryFacts={butlerRecordFacts(property)}
        lastUpdateWords={describeLastUpdate(property.updatedAt)}
        titleRow={(
          <TitleRow
            backTo="/properties"
            backWords="Back to properties"
            title={nameWords}
            under={placeWords && placeWords !== nameWords ? placeWords : ''}
            record={property}
            layout={layout}
            onSaveField={saveOneField}
            actions={actions}
          />
        )}
        overviewTop={(
          <>
            <SummaryCard record={property} layout={layout} onSaveField={saveOneField} />
            <SidePanels>
              <PersonPanel title="Owner" person={person} />
              <NextActionPanel sorted={sortedActivities} />
              <LatestThingPanel title="Reports" {...latestReportPanelParts(reportAnswer)} />
            </SidePanels>
            <DetailsBlock record={property} sections={layout.sections} onSaveField={saveOneField} />
          </>
        )}
        timeline={<HistoryTimeline days={sortedActivities ? sortedActivities.history : null} />}
      />
    </HrPageFrame>
  );
}

const ZohoRecordPages = { ZohoCustomerRecordPage, ZohoPropertyRecordPage };

export default ZohoRecordPages;
